import {computed, makeObservable, observable, runInAction} from 'mobx';
import {makeLoggable} from 'mobx-log';

import {AirtableFeedItem} from '../models/airtable';
import apiService from '../transport/apiService';
import {distributeVideos} from '../utils/feedDistribute';

import type {RootStore} from './rootStore';

export type Segment = {value: string; label: string};

const ALL_SEGMENT: Segment = {value: 'ALL', label: 'ALL'};

export class FeedStore {
  rootStore: RootStore;

  feedList: AirtableFeedItem[] = [];
  discoverList: AirtableFeedItem[] = [];
  /**
   * Raw, unfiltered feed payload from /content/feed. Drives `segments`
   * fallback when serverSegments is empty so DiscoverFilters surfaces every
   * category present in the payload.
   */
  allItems: AirtableFeedItem[] = [];

  /**
   * Server-driven segments list from /content/discover/segments
   * (re-activated for DIS-1306 via `useFeedSegments`). Takes precedence
   * over the client-side dedup-from-items fallback so the tab strip stays
   * accurate when the paginated payload no longer carries the full
   * universe of items.
   */
  serverSegments: string[] = [];

  selectedSegment: Segment = ALL_SEGMENT;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeObservable(this, {
      feedList: observable,
      discoverList: observable,
      allItems: observable,
      serverSegments: observable,
      selectedSegment: observable,
      filteredDiscoverList: computed,
      segments: computed,
    });
    makeLoggable(this);
  }

  /**
   * Filter categories shown in DiscoverFilters. Derived from the raw fetched
   * feed payload (allItems) so all categories represented in /content/feed
   * appear in the filter — matches the legacy /content/discover/segments
   * endpoint behaviour that DiscoverFilters relied on pre-DIS-1201. Falls
   * back to discoverList while allItems is still empty (e.g. persisted
   * cache restore before live fetch). Always prepends ALL. Drops empty /
   * whitespace entries and dedupes case-insensitively (DIS-1209).
   */
  get segments(): Segment[] {
    if (this.serverSegments.length > 0) {
      const seen = new Set<string>([ALL_SEGMENT.value.toUpperCase()]);
      const cleaned: Segment[] = [];
      for (const raw of this.serverSegments) {
        if (typeof raw !== 'string') continue;
        const trimmed = raw.trim();
        if (trimmed.length === 0) continue;
        const key = trimmed.toUpperCase();
        if (seen.has(key)) continue;
        seen.add(key);
        cleaned.push({value: trimmed, label: trimmed});
      }
      return [ALL_SEGMENT, ...cleaned];
    }
    const allItems = this.allItems ?? [];
    const discoverList = this.discoverList ?? [];
    const source = allItems.length > 0 ? allItems : discoverList;
    const seen = new Set<string>([ALL_SEGMENT.value.toUpperCase()]);
    const cleaned: Segment[] = [];
    for (const item of source) {
      const raw = (item as unknown as {segment?: string}).segment;
      if (typeof raw !== 'string') continue;
      const trimmed = raw.trim();
      if (trimmed.length === 0) continue;
      const key = trimmed.toUpperCase();
      if (seen.has(key)) continue;
      seen.add(key);
      cleaned.push({value: trimmed, label: trimmed});
    }
    return [ALL_SEGMENT, ...cleaned];
  }

  setServerSegments = (list: string[] | undefined) => {
    this.serverSegments = list ?? [];
  };

  /**
   * Legacy entry point — segments are now derived client-side from
   * allItems (see `get segments`). Retained as a no-op that resets the
   * active filter to ALL so existing callers (rootStore init waterfall,
   * DiscoverListScreen refresh) don't need to change.
   */
  getSegments = async (_storeNumber?: string, _storeState?: string) => {
    runInAction(() => {
      this.selectedSegment = ALL_SEGMENT;
    });
  };

  selectSegment = (segment: Segment) => {
    // Wrap in an action: selectedSegment is observed by DiscoverListScreen
    // (observer), so mutating it outside an action trips MobX's default
    // enforceActions strict-mode warning on every filter tap. Mirrors
    // getSegments' runInAction; selectSegment isn't annotated as an action.
    runInAction(() => {
      this.selectedSegment = segment;
    });
  };

  get filteredDiscoverList() {
    const baseList =
      this.selectedSegment.value === 'ALL'
        ? this.discoverList
        : this.discoverList.filter(d => d.segment === this.selectedSegment.value);
    return distributeVideos(baseList);
  }

  loadLinkedProducts = async (article: Pick<AirtableFeedItem, 'productBarcodes'>) => {
    if (article.productBarcodes?.length > 0) {
      return await apiService.searchByBarcode(article.productBarcodes);
    } else {
      return [];
    }
  };
}
